import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, money, fmtDate, PageHead, FinanceEmpty, exportExcel, PrintStyle, printArea, EXPENSE_CATS, REVENUE_CATS } from './shared';
import Icon from '../../../components/dashboard/DashboardIcons';
import PeriodRangePicker from '../../../components/ui/PeriodRangePicker';
import { resolvePreset } from '../../../lib/dashboardPeriods';

const REPORTS = [
  { k: 'income', l: 'الدخل والمصروفات' },
  { k: 'receivables', l: 'المستحقات' },
];

export default function FinanceReportsPage() {
  const [report, setReport] = useState('income');
  const [range, setRange] = useState(() => resolvePreset('all'));
  const from = range.startDate;
  const to = range.endDate;
  const [expenses, setExpenses] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [e, r, i] = await Promise.allSettled([
      axios.get(`${API}/expenses`, auth()),
      axios.get(`${API}/revenues`, auth()),
      axios.get(`${API}/club-invoices?limit=100`, auth()),
    ]);
    if (e.status === 'fulfilled') setExpenses(e.value.data || []);
    if (r.status === 'fulfilled') setRevenues(r.value.data || []);
    if (i.status === 'fulfilled') setInvoices(i.value.data?.items || []);
    if ([e, r].some((x) => x.status === 'rejected' && x.reason?.response?.status === 403)) setError('لا تملك صلاحية الوصول للتقارير المالية');
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const inRange = (d) => (!from || d >= from) && (!to || d <= to);
  const exp = expenses.filter((x) => x.status !== 'rejected' && inRange(x.expense_date));
  const rev = revenues.filter((x) => inRange(x.revenue_date));
  const receivables = invoices.filter((i) => ['unpaid', 'partial'].includes(i.status));

  if (error) return <div className="rounded-xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-600" data-testid="reports-error">{error}</div>;

  const incomeRows = [
    ...rev.map((x) => ({ 'النوع': 'إيراد', 'البيان': x.title, 'التصنيف': REVENUE_CATS[x.category] || x.category, 'التاريخ': x.revenue_date, 'المبلغ': x.amount })),
    ...exp.map((x) => ({ 'النوع': 'مصروف', 'البيان': x.title, 'التصنيف': EXPENSE_CATS[x.category] || x.category, 'التاريخ': x.expense_date, 'المبلغ': -x.amount })),
  ].sort((a, b) => new Date(b['التاريخ']) - new Date(a['التاريخ']));

  const recvRows = receivables.map((i) => ({ 'الفاتورة': i.invoice_number, 'العميل': i.client_name, 'الإجمالي': i.total, 'المدفوع': i.paid_amount || 0, 'المتبقي': i.total - (i.paid_amount || 0), 'الاستحقاق': i.due_date || '' }));

  const activeRows = report === 'income' ? incomeRows : recvRows;

  return (
    <div className="mx-auto max-w-6xl space-y-4 print-area" data-testid="finance-reports-page">
      <PrintStyle />
      <PageHead title="التقارير المالية" desc="تقارير مبنية على البيانات الفعلية للمنشأة"
        action={<div className="flex flex-wrap gap-2 print:hidden">
          <PeriodRangePicker value={range} onChange={setRange} theme="light" testId="report-period" />
          <button onClick={() => exportExcel(activeRows, `تقرير-${report}`)} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600" data-testid="report-export-btn"><Icon name="chart" className="w-4 h-4" /> Excel</button>
          <button onClick={printArea} className="flex items-center gap-2 rounded-lg bg-[#16233b] px-3 py-2 text-sm font-semibold text-white" data-testid="report-print-btn"><Icon name="document" className="w-4 h-4" /> طباعة</button>
        </div>} />

      <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 print:hidden" data-testid="report-tabs">
        {REPORTS.map((r) => <button key={r.k} onClick={() => setReport(r.k)} data-testid={`report-tab-${r.k}`} className={`flex-1 rounded-md px-3 py-1.5 text-sm ${report === r.k ? 'bg-[#16233b] text-white font-semibold' : 'text-slate-600'}`}>{r.l}</button>)}
      </div>

      {loading ? <div className="h-40 animate-pulse rounded-xl bg-slate-200/70" /> : report === 'income' ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">الإيرادات</div><div className="text-lg font-bold text-teal-700" dir="rtl">{money(rev.reduce((s, x) => s + x.amount, 0))}</div></div>
            <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">المصروفات</div><div className="text-lg font-bold text-red-600" dir="rtl">{money(exp.reduce((s, x) => s + x.amount, 0))}</div></div>
            <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">الصافي</div><div className="text-lg font-bold text-slate-900" dir="rtl" data-testid="report-net">{money(rev.reduce((s, x) => s + x.amount, 0) - exp.reduce((s, x) => s + x.amount, 0))}</div></div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white">
            {incomeRows.length === 0 ? <div className="p-6"><FinanceEmpty text="لا توجد عمليات في الفترة المحددة" /></div> : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500"><th className="px-4 py-2.5">النوع</th><th className="px-4 py-2.5">البيان</th><th className="hidden px-4 py-2.5 md:table-cell">التصنيف</th><th className="px-4 py-2.5">التاريخ</th><th className="px-4 py-2.5">المبلغ</th></tr></thead>
                <tbody>{incomeRows.slice(0, 200).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid="report-row">
                    <td className="px-4 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r['المبلغ'] >= 0 ? 'bg-teal-50 text-teal-700' : 'bg-red-50 text-red-600'}`}>{r['النوع']}</span></td>
                    <td className="px-4 py-2 text-slate-800">{r['البيان']}</td>
                    <td className="hidden px-4 py-2 text-slate-500 md:table-cell">{r['التصنيف']}</td>
                    <td className="px-4 py-2 text-slate-600">{fmtDate(r['التاريخ'])}</td>
                    <td className={`px-4 py-2 font-bold ${r['المبلغ'] >= 0 ? 'text-teal-700' : 'text-red-600'}`} dir="rtl">{money(Math.abs(r['المبلغ']))}</td>
                  </tr>))}</tbody>
              </table></div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          {recvRows.length === 0 ? <div className="p-6"><FinanceEmpty text="لا توجد مستحقات غير محصلة" /></div> : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500"><th className="px-4 py-2.5">الفاتورة</th><th className="px-4 py-2.5">العميل</th><th className="px-4 py-2.5">الإجمالي</th><th className="px-4 py-2.5">المتبقي</th><th className="hidden px-4 py-2.5 md:table-cell">الاستحقاق</th></tr></thead>
              <tbody>{recvRows.map((r, i) => (
                <tr key={i} className="border-b border-slate-50" data-testid="report-recv-row">
                  <td className="px-4 py-2 font-bold" dir="ltr">{r['الفاتورة']}</td><td className="px-4 py-2">{r['العميل']}</td>
                  <td className="px-4 py-2" dir="rtl">{money(r['الإجمالي'])}</td><td className="px-4 py-2 font-bold text-amber-600" dir="rtl">{money(r['المتبقي'])}</td>
                  <td className="hidden px-4 py-2 text-slate-600 md:table-cell">{r['الاستحقاق'] ? fmtDate(r['الاستحقاق']) : '—'}</td>
                </tr>))}</tbody>
            </table></div>
          )}
        </div>
      )}
    </div>
  );
}
